import {
  ApplicationConfig,
  EnvironmentProviders,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  Provider,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LfTranslateLoaderService, provideLfUiTranslation } from '@leafio/ui/i18n';
import { AppTitleStrategy } from '@services/app-title-strategy/app-title-strategy';
import { NavigatorAssistant } from '@services/assistant/navigator-assistant/navigator-assistant';
import { LfScrollbarStrategyService } from '@leafio/ui/utils';
import { CompanyService } from '@services/company/company.service';
import { UrlInterceptor } from '@interceptors/url.interceptor';
import { HeaderInterceptor } from '@interceptors/header.interceptor';
import { AuthInterceptor } from '@interceptors/auth.interceptor';
import { CurrentUserService } from '@services/current-user/current-user';
import { DEFAULT_LOCALE } from '@constants/default-locale';
import { LF_DEFAULT_FIELD_LABEL_UPPERCASE, LF_TRIM_INPUTS, LF_VALIDATION_ERROR_KEY_RESOLVER } from '@leafio/ui/forms';
import { LF_CONFIRM_ACTION_DIALOG_DEFAULT_CONFIG } from '@leafio/ui/dialog';
import { FORM_VALIDATION_ERRORS_RESOLVER } from '@constants/form-validation-errors';
import { LfLoaderService } from '@leafio/ui/loader';
import { LF_DEFAULT_TOAST_TITLES } from '@leafio/ui/toasts';
import { LF_PAGINATOR_CONFIG, LfPaginatorIntlService } from '@leafio/ui/paginator';
import { PaginatorIntl } from '@services/paginator-intl/paginator-intl';
import { DEFAULT_PER_PAGE } from '@constants/query-params';

const CORE_PROVIDERS = [
  provideBrowserGlobalErrorListeners(),
  provideZonelessChangeDetection(),
  provideAnimations(),
  provideHttpClient(withInterceptorsFromDi()),
  {
    provide: TitleStrategy,
    useClass: AppTitleStrategy,
  },
  provideAppInitializer(() => {
    return inject(CompanyService).init();
  }),
];

const UI_KIT_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  provideLfUiTranslation({
    path: './assets/i18n',
    defaultLocale: DEFAULT_LOCALE,
  }),
  provideAppInitializer(async () => {
    const currentUserService = inject(CurrentUserService);
    const translateService = inject(LfTranslateLoaderService);

    await currentUserService.initialize();
    await translateService.init(currentUserService.locale());
  }),
  provideAppInitializer(() => {
    inject(LfScrollbarStrategyService).use('auto');
  }),
  { provide: LF_TRIM_INPUTS, useValue: true },
  { provide: LF_DEFAULT_FIELD_LABEL_UPPERCASE, useValue: false },
  {
    provide: LF_CONFIRM_ACTION_DIALOG_DEFAULT_CONFIG,
    useValue: {
      confirmBtnText: 'BTN_CONFIRM',
      cancelBtnText: 'BTN_CANCEL',
      btnSize: 'm',
    },
  },
  {
    provide: LF_VALIDATION_ERROR_KEY_RESOLVER,
    useValue: FORM_VALIDATION_ERRORS_RESOLVER,
  },
  {
    provide: LF_DEFAULT_TOAST_TITLES,
    useValue: {
      success: '',
    },
  },
  provideAppInitializer(() => {
    inject(LfLoaderService).init({
      hidingDelay: 100,
    });
  }),

  { provide: LfPaginatorIntlService, useClass: PaginatorIntl },
  {
    provide: LF_PAGINATOR_CONFIG,
    useValue: {
      hidePageInfo: false,
      pageSize: DEFAULT_PER_PAGE,
      pageSizeOptions: [10, 25, 50, 100],
      pageSizeEditable: false,
      showFirstLastButtons: true,
      showPrevNextButtons: true,
      buttonsLength: 5,
      zeroBasedIndexing: false,
    },
  },
];

const INTERCEPTORS: Provider | EnvironmentProviders[] = [
  { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: UrlInterceptor, multi: true },
];

export const appConfig: ApplicationConfig = {
  providers: [
    ...CORE_PROVIDERS,
    ...UI_KIT_PROVIDERS,
    ...INTERCEPTORS,
    provideRouter(NavigatorAssistant.processRoutes(routes)),
  ],
};
